import { FC, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import MDEditor from '@uiw/react-md-editor';
import PageHeader from '@/components/ui/PageHeader/PageHeader';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import { useAdminQuestionCategory, useUpdateQuestionCategory, Tip } from '@/api/useAdminQuestionCategories';
import Routes from '@/router/Routes';

interface CollapsibleSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: FC<CollapsibleSectionProps> = ({ title, isOpen, onToggle, children }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-lg font-medium text-gray-900">{title}</span>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

const EditQuestionCategoryPage: FC = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const { data: category, isLoading } = useAdminQuestionCategory(categoryId);
  const { mutate: updateCategory, isPending: isSaving } = useUpdateQuestionCategory();

  const [contentOpen, setContentOpen] = useState(true);
  const [tipsOpen, setTipsOpen] = useState(true);
  const [content, setContent] = useState('');
  const [tips, setTips] = useState<Tip[]>([]);

  useEffect(() => {
    if (category) {
      setContent(category.content || '');
      setTips(category.tips || []);
    }
  }, [category]);

  const handleBack = () => {
    navigate(Routes.ADMIN_CATEGORIES);
  };

  const handleSave = () => {
    if (!categoryId) return;

    updateCategory(
      {
        id: categoryId,
        content: content || null,
        tips: tips.length > 0 ? tips : null,
      },
      {
        onSuccess: () => {
          navigate(Routes.ADMIN_CATEGORIES);
        },
      }
    );
  };

  const handleAddTip = () => {
    setTips([...tips, { title: '', content: '' }]);
  };

  const handleUpdateTip = (index: number, field: 'title' | 'content', value: string) => {
    const newTips = [...tips];
    newTips[index] = { ...newTips[index], [field]: value };
    setTips(newTips);
  };

  const handleDeleteTip = (index: number) => {
    setTips(tips.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="loading loading-spinner loading-lg text-[#C87941]"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-gray-500">Category not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Header */}
      <PageHeader
        title=""
        showBackButton
        onBackClick={handleBack}
        rightContent={
          <LoadingButton
            loading={isSaving}
            onClick={handleSave}
            className="bg-[#0f172a] hover:bg-[#1e293b] text-white border-none px-6"
          >
            Accept & Save
          </LoadingButton>
        }
      />

      {/* Category Info */}
      <div className="bg-white rounded-3xl mt-6 px-8 py-6">
        <div className="grid grid-cols-5 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {category.subject}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Paper</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {category.paper_number}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900 font-mono font-medium">
              {category.category}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {category.name}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Code</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-500 font-mono text-sm">
              {category.category_code}
            </div>
          </div>
        </div>
        {category.description && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-600">
              {category.description}
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="mt-6">
        <CollapsibleSection
          title="Content"
          isOpen={contentOpen}
          onToggle={() => setContentOpen(!contentOpen)}
        >
          <div data-color-mode="light">
            <MDEditor
              value={content}
              onChange={(value) => setContent(value || '')}
              height={300}
              preview="live"
            />
          </div>
        </CollapsibleSection>
      </div>

      {/* Tips Section */}
      <div className="mt-6">
        <CollapsibleSection
          title="Tips"
          isOpen={tipsOpen}
          onToggle={() => setTipsOpen(!tipsOpen)}
        >
          <div className="space-y-6">
            {tips.map((tip, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">Tip {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteTip(index)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={tip.title}
                    onChange={(e) => handleUpdateTip(index, 'title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C87941] focus:border-transparent"
                    placeholder="Enter tip title..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                  <div data-color-mode="light">
                    <MDEditor
                      value={tip.content}
                      onChange={(value) => handleUpdateTip(index, 'content', value || '')}
                      height={200}
                      preview="live"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddTip}
              className="flex items-center gap-2 px-4 py-2 text-[#C87941] font-medium hover:bg-orange-50 rounded-xl transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Add Tip
            </button>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
};

export default EditQuestionCategoryPage;
